import React, { FC, Ref, createRef, useState } from 'react'
import Link from 'next/link'
import styles from './TrophyCard.module.scss'
import { useTranslation } from 'next-i18next'
import { SafePlayVideo, SafeStopVideo } from '~/modules/Shared/Infrastructure/SafeVideoElement'
import { ComponentTrophyDto } from '~/modules/Products/Infrastructure/Dtos/ComponentTrophyDto'

interface Props {
  trophyDto: ComponentTrophyDto
  backlink?: boolean
  appearance?: 'default' | 'gallery' | 'museumGallery'
}

export const TrophyCard: FC<Props> = ({ trophyDto, backlink, appearance = 'default' }) => {
  const { t } = useTranslation('trophies')
  const player: Ref<HTMLVideoElement> = createRef()
  const [playPromise, setPlayPromise] = useState<Promise<void>>(Promise.resolve())
  const isGalleryAppearance = appearance === 'gallery'
  const isMuseumGalleryAppearance = appearance === 'museumGallery'
  const museumGalleryTitle = trophyDto.content.header || trophyDto.content.title
  const museumGalleryCategory = trophyDto.discipline || trophyDto.content.subtitle.split('|')[0].trim()
  const museumGalleryOwner = trophyDto.content.title || trophyDto.content.subtitle
  const mediaClassName = [
    styles.trophyCard__media,
    isGalleryAppearance ? styles.trophyCard__media_gallery : '',
    isMuseumGalleryAppearance ? styles.trophyCard__media_museumGallery : '',
  ].filter(Boolean).join(' ')
  const containerClassName = [
    styles.trophyCard__container,
    isGalleryAppearance ? styles.trophyCard__container_gallery : '',
    isMuseumGalleryAppearance ? styles.trophyCard__container_museumGallery : '',
  ].filter(Boolean).join(' ')
  const imageWrapperClassName = [
    styles.trophyCard__imageWrapper,
    isGalleryAppearance ? styles.trophyCard__imageWrapper_gallery : '',
    isMuseumGalleryAppearance ? styles.trophyCard__imageWrapper_museumGallery : '',
  ].filter(Boolean).join(' ')
  const dataClassName = [
    styles.trophyCard__data,
    isGalleryAppearance ? styles.trophyCard__data_gallery : '',
    isMuseumGalleryAppearance ? styles.trophyCard__data_museumGallery : '',
  ].filter(Boolean).join(' ')
  const cardContent = isMuseumGalleryAppearance
    ? (
      <>
        <div className={ `${styles.trophyCard__fullName} ${styles.trophyCard__fullName_museumGallery}` }>
          { museumGalleryTitle }
        </div>
        <div className={ `${styles.trophyCard__competition} ${styles.trophyCard__competition_museumGallery}` }>
          { museumGalleryCategory }
        </div>
        <div className={ `${styles.trophyCard__extra} ${styles.trophyCard__extra_museumGallery}` }>
          { museumGalleryOwner }
        </div>
      </>
      )
    : isGalleryAppearance
      ? (
        <>
          <div className={ `${styles.trophyCard__fullName} ${styles.trophyCard__fullName_gallery}` }>
            { trophyDto.content.header }
          </div>
          <div className={ `${styles.trophyCard__competition} ${styles.trophyCard__competition_gallery}` }>
            { trophyDto.discipline }
          </div>
          <div className={ `${styles.trophyCard__extra} ${styles.trophyCard__extra_gallery}` }>
            { trophyDto.content.subtitle }
          </div>
        </>
        )
      : (
        <>
          <div className={ styles.trophyCard__competition }>
            { trophyDto.content.header }
          </div>
          <div className={ styles.trophyCard__fullName }>
            { trophyDto.content.title }
          </div>
          <div className={ styles.trophyCard__extra }>
            { trophyDto.content.subtitle }
          </div>
        </>
        )

  let media

  if (trophyDto.animation !== null) {
    media = (
      <video
        className={ mediaClassName }
        preload={ 'none' }
        poster={ trophyDto.imageUrl }
        loop={ true }
        autoPlay={ false }
        muted={ true }
        ref={ player }
      >
        <source
          src={ trophyDto.animation.url }
          type={ trophyDto.animation.type }
        />
      </video>
    )
  } else {
    media = (
      <img
        className={ mediaClassName }
        src={ trophyDto.imageUrl }
        alt={ trophyDto.description }
      />
    )
  }

  return (
    <div
      data-qa="trophy-card-container"
      className={ containerClassName }
      onMouseOver={ async () => player.current ? await SafePlayVideo(player.current, setPlayPromise) : '' }
      onMouseLeave={ () => player.current ? SafeStopVideo(player.current, playPromise) : '' }
    >
      <Link
        href={ backlink ? `/trophies/${trophyDto.id}?backlink` : `/trophies/${trophyDto.id}` }
        className={ styles.trophyCard__link }
      >
        { t('show_info_button') }
      </Link>

      { !isGalleryAppearance && !isMuseumGalleryAppearance && (
        <div
          className={ styles.trophyCard__discipline }
        >
          { trophyDto.discipline }
        </div>
      ) }

      <div
        data-qa="media-container"
        className={ imageWrapperClassName }>
        { media }
      </div>

      <div
        className={ dataClassName }
      >
        { cardContent }
      </div>
    </div>
  )
}
